import { MultiBreakdownOptionMixin } from '../../mixin';
import breakdownTranslatorMixin from '@/mixins/translator/breakdown';

import QuickControlDropdown from '../Dropdown/index';
import { useFieldGuard } from '../useFieldGuard';

import * as S from '../styles';

import { IFRAME_EVENT_MESSAGE } from '@consts';
import { MAX_QUICK_CONTROL_BREAKDOWN_COUNT } from '../../consts';
import {
    isCustomBreakdown,
    makeBreakdownFormat,
    normalizeCustomBreakdown,
} from '../../utils';

export default {
    name: 'BreakdownQuickControl',
    mixins: [MultiBreakdownOptionMixin, breakdownTranslatorMixin],
    setup(props, { emit }) {
        const mixinSetup = MultiBreakdownOptionMixin.setup(props);
        useFieldGuard({
            getSelectedId: () => props.selectedField,
            getEditableIds: () => props.exposureList,
            getFixedIds: () => props.fixedList,
            getDefaultIds: () => props.defaultList,
            emitUpdateList: (newList) =>
                emit('update:exposureListMap', {
                    key: props.exposureListKey,
                    value: newList,
                }),
            emitUpdateSelected: (newSelected) =>
                emit('update:exposureSelectedFieldMap', newSelected),
        });

        return mixinSetup;
    },
    props: {
        panelKey: String,
        optionTitle: String,
        dropdownDisabled: Boolean,
        hide: { type: Boolean, default: false },
        /**
         * 선택된 breakdown key (string|number)
         */
        selectedField: { type: String | Number, default: '' },
        /**
         * exposureListMap 에서 사용할 key
         */
        exposureListKey: { type: String, required: true },
        /**
         * 선택된 breakdown key 목록 ((string|number)[] | undefined)
         * undefined = 미설정 상태 (default list 사용 중)
         */
        exposureList: { type: Array, default: undefined },
        /**
         * exposureList가 undefined일 때 표시할 기본 목록 (string[])
         */
        defaultList: { type: Array, default: () => [] },
        /**
         * 드롭다운에 항상 고정 표시할 옵션 목록 (string[])
         */
        fixedList: { type: Array, default: () => [] },
        canEditCustomBreakdown: { type: Boolean, default: true },
    },
    data() {
        return {
            isFromQuickControl: true,
            iframeListeners_: [
                {
                    eventType: IFRAME_EVENT_MESSAGE.UPDATE_BREAKDOWN_PANEL,
                    listener: (e) => {
                        const { data } = e;
                        if (!data || data.panelKey !== this.panelKey) return;

                        const { selectedFields } = data;

                        // 위젯 저장 시 custom breakdown id는 number 타입으로 저장
                        // (브레이크다운 패널 v3 API는 key를 string으로 반환하므로,
                        //  패널에서 선택한 값이 '123' 같은 string으로 넘어올 수 있음)
                        const normalizedFields = selectedFields.map((v) =>
                            isCustomBreakdown(v)
                                ? normalizeCustomBreakdown(v)
                                : v
                        );

                        this.$emit('update:exposureListMap', {
                            key: this.exposureListKey,
                            value: normalizedFields,
                        });
                        // selected reset은 패널이 닫힐 때 useFieldGuard가 처리
                    },
                },
            ],
        };
    },
    computed: {
        title() {
            return this.$t(
                'FIELD_PANEL.breakdown_exposure_setting.second_panel_title',
                { option: this.optionTitle }
            );
        },
        mainGuide() {
            return this.$t(
                'FIELD_PANEL.breakdown_exposure_setting.second_panel_main_guide'
            );
        },
        subGuide() {
            return this.$t(
                'FIELD_PANEL.breakdown_exposure_setting.second_panel_sub_guide'
            );
        },
        /**
         * Override mixin: openReactPanel/syncSelectedFields 에서 사용
         * undefined(미설정) 상태에서는 defaultList를 현재 선택 목록으로 간주
         */
        selectedFields() {
            return this.exposureList ?? this.defaultList;
        },
        limitCount() {
            return MAX_QUICK_CONTROL_BREAKDOWN_COUNT;
        },
    },
    methods: {
        handleListUpdate(newList) {
            this.$emit('update:exposureListMap', {
                key: this.exposureListKey,
                value: newList,
            });
        },
        handleRemoveItem(key) {
            const updated = (this.exposureList ?? this.defaultList).filter(
                (v) => v !== key
            );

            this.$emit('update:exposureListMap', {
                key: this.exposureListKey,
                value: updated,
            });

            if (this.selectedField === key) {
                this.$emit(
                    'update:exposureSelectedFieldMap',
                    updated[0] ?? this.fixedList[0] ?? ''
                );
            }
        },
        handleSelectItem(key) {
            this.$emit('update:exposureSelectedFieldMap', key);
        },
        renderItem() {
            const {
                exposureList,
                defaultList,
                fixedList,
                selectedField,
                dropdownDisabled,
                getBreakdownName,
            } = this;

            return (
                <S.ExposureDropdownWrapper>
                    <QuickControlDropdown
                        fieldType="breakdown"
                        exposureList={exposureList ?? defaultList}
                        selectedValue={selectedField}
                        translator={(key) =>
                            getBreakdownName(makeBreakdownFormat(key))
                        }
                        fixedList={fixedList}
                        disabled={dropdownDisabled}
                        on={{
                            'update:list': this.handleListUpdate,
                            'remove:item': this.handleRemoveItem,
                            'select:item': this.handleSelectItem,
                        }}
                    />
                </S.ExposureDropdownWrapper>
            );
        },
    },
    render() {
        const { optionTitle, wrapOptions, renderItem } = this;

        return (
            <S.ComponentWrapper hide={this.hide}>
                {wrapOptions({
                    headerContent: renderItem(),
                    title: optionTitle,
                })}
            </S.ComponentWrapper>
        );
    },
};
